import { Button, Pagination, Spin } from 'antd'
import { match } from 'ts-pattern'
import { Trans } from '@lingui/react/macro'

import ListItem from './ListItem'

import type { $Enums } from '@prisma/client'
import type { FileFormValue } from '../UploadAudio'
import type { EditAudioShowData } from './ListItem'

import TipsBaseModal from '@/pages/tad.ai/_blocks/Modals/Tips/TipsBaseModal'
import LazyImage from '@/components/LazyImage'


import { useState, useEffect } from 'react';
import { api } from '@/utils/api';
import { cls } from 'tagged-classnames-free';
const PAGE_SIZE = 10

export interface HistoryModalProps {
  open: boolean
  onClose: () => void
  musicModel: Extract<$Enums.MusicModel, 'V2' | 'V3'>
  musicType: $Enums.MusicType
  onUse: (data: FileFormValue) => void
  onEditAudio?: (data: EditAudioShowData) => void
}

function HistoryModal(props: HistoryModalProps) {
  const { open, onClose, musicModel, musicType, onUse, onEditAudio } = props

  const [current, setCurrent] = useState(1)

  useEffect(() => {
    setCurrent(1)
  }, [open])

  const {
    data: historyData,
    isFetching,
    refetch,
  } = api.reference.history.useQuery(
    {
      musicModel,
      page: current,
      limit: PAGE_SIZE,
      musicType,
    },
    {
      enabled: !!musicModel && open,
    },
  )

  const doRenderPagination =
    typeof historyData?.total === 'number' && historyData.total > PAGE_SIZE

  const handleUse = (data: FileFormValue) => {
    onUse(data)
    onClose()
  }

  return (
    <TipsBaseModal open={open} onCancel={onClose} destroyOnHidden>
      <div>
        <div className={cls`text-f-text mb-4 text-lg font-semibold`}>
          <Trans>My Uploads</Trans>
        </div>
        <Spin spinning={isFetching}>
          <div className={cls`h-fit min-h-[250px]`}>
            {match(!!historyData?.data?.length)
              .with(true, () => (
                <ul
                  className={cls`scrollbar h-fit max-h-[60vh] w-full space-y-2 overflow-y-auto`}
                >
                  {historyData?.data?.map((v) => (
                    <ListItem
                      key={v.token}
                      data={v}
                      musicModel={musicModel}
                      musicType={musicType}
                      onUse={handleUse}
                      onEditAudio={onEditAudio}
                      onDeleteSuccess={() => {
                        if (historyData?.data?.length === 1 && current > 1) {
                          setCurrent(current - 1)
                        }
                        refetch()
                      }}
                    />
                  ))}
                </ul>
              ))
              .otherwise(() => (
                <div
                  className={cls`mx-auto flex max-w-[318px] flex-col items-center justify-center gap-1 pt-6 text-center`}
                >
                  <div className={cls`relative`}>
                    <LazyImage
                      src='/images/images-empty.png'
                      width={318}
                      height={77}
                      alt='empty'
                    />
                  </div>
                  <div
                    className={cls`text-f-text-secondary text-base font-semibold`}
                  >
                    <Trans>It's empty in here.</Trans>
                  </div>
                  <div className={cls`text-f-text-quaternary text-sm`}>
                    <Trans>Upload your first audio clip to get started.</Trans>
                  </div>
                  <Button
                    type='primary'
                    className={cls`my-5 flex h-8 min-w-[120px] items-center justify-center px-5`}
                    onClick={onClose}
                  >
                    <Trans>Upload Now</Trans>
                  </Button>
                </div>
              ))}
          </div>
        </Spin>
        {doRenderPagination && (
          <div className={`flex justify-end pt-4`}>
            <Pagination
              size='small'
              hideOnSinglePage
              pageSize={PAGE_SIZE}
              showSizeChanger={false}
              showQuickJumper
              total={historyData?.total}
              current={current}
              onChange={(current) => {
                setCurrent(current)
              }}
            />
          </div>
        )}
      </div>
    </TipsBaseModal>
  )
}

export default HistoryModal
export type { EditAudioShowData } from './ListItem'
